import React, { useContext } from 'react';
import { ChatContext } from 'stream-chat-react';
import { Avatar } from '@components/shared/Avatar';
import { useTranslate } from 'nucleus-text';
import { injectTestId } from '@cvent/nucleus-test-automation';
import useTheme from '@cvent/blocks/hooks/useTheme';
import useTimezone from '@hooks/useTimezone';
import { isBefore, sub } from 'date-fns';
import { useAttendeeMessaging } from '@context/AttendeeMessagingContext';
import Link from 'next/link';
import { useRouter } from 'next/router';
import useCardStyles from '@cvent/blocks/hooks/useCardStyles';
import injectCustomDatadogActionName from '@tools/injectCustomDatadogActionName';
import { CSSObject } from '@emotion/react';
import { Channel, ChannelMemberResponse } from 'stream-chat';
import { StreamType } from '@components/chat/shared/useStreamChat';
import Icon from '@components/shared/Icon';
import overrideSessionReplayPrivacyLevel, { PRIVACY_LEVEL } from '@tools/overrideSessionReplayPrivacyLevel';
import { useChannelName } from './useChannelName';
import { ChannelType } from './constants';

const dateTimeFormat = {
  hour: 'numeric',
  minute: 'numeric'
};

const useTimeStamp = (channel: Channel<StreamType>) => {
  const { date } = useTimezone();
  if (!channel.state.last_message_at) {
    return '';
  }

  // If last_message_at > 24 hours, display date. Else, display am/pm
  const currentDateTimeMinusOneDay = sub(new Date(), { days: 1 });
  const displayDateTime = isBefore(channel.state.last_message_at, currentDateTimeMinusOneDay)
    ? date(channel.state.last_message_at, undefined)
    : date(channel.state.last_message_at, dateTimeFormat);
  return displayDateTime;
};

const getNameInStream = (members: ChannelMemberResponse<StreamType>[]): { firstName?: string; lastName?: string } => {
  if (members.length === 1) {
    return {
      firstName: members[0].user.firstName,
      lastName: members[0].user.lastName
    };
  }
  return {};
};

const useStyle = ({ isActiveChannel, hasUnreadMessages, variant }): Record<string, CSSObject> => {
  const { borderColor, font, size } = useTheme();
  const isCompact = variant === 'compact';
  const cardStyles = useCardStyles();
  return {
    container: {
      border: isActiveChannel ? cardStyles.border : 'none',
      background: isActiveChannel ? cardStyles.backgroundColor : 'transparent',
      textAlign: 'left',
      color: font.color.base,
      width: '100%',
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      margin: '8px 0',
      padding: '8px 24px',
      cursor: 'pointer',
      fontWeight: hasUnreadMessages ? font.base.weight.bold : font.base.weight.regular,
      borderRadius: cardStyles.borderRadius
    },
    linkContainer: {
      textDecoration: 'none',
      color: font.color.base,
      width: '100%',
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      margin: '8px 0',
      // needs 8px on the left so the unread indicator appears correctly
      padding: isCompact ? '8px 8px 8px 16px' : '8px 8px 8px 20px',
      fontWeight: hasUnreadMessages ? font.base.weight.bold : font.base.weight.regular
    },
    avatarWrapper: {
      alignSelf: isCompact ? 'center' : 'flex-start'
    },
    unreadMessagesIndicator: {
      position: 'absolute',
      left: isCompact ? '0px' : '8px',
      top: isCompact ? "calc('50%' - 4px)" : '50%',
      transform: 'translateY(-50%)',
      width: '8px',
      height: '8px',
      borderRadius: '100%',
      backgroundColor: font.color.interactive.base
    },
    contentWrapper: {
      display: 'flex',
      flexGrow: '1',
      minWidth: 0,
      flexDirection: 'column',
      marginLeft: '8px',
      ...(isCompact
        ? {
            borderBottom: `${size.border.base}px solid ${borderColor.soft}`,
            paddingBottom: '12px',
            marginBottom: '-8px'
          }
        : {})
    },
    channelNameWrapper: {
      display: 'flex',
      justifyContent: 'space-between'
    },
    channelName: {
      fontFamily: isCompact ? font.base.family : null,
      fontWeight: font.base.weight.bold,
      fontSize: isCompact ? font.base.size['4'] : null
    },
    time: {
      fontFamily: isCompact ? font.base.family : null,
      fontSize: font.base.size.xxs,
      color: font.color.soft
    },
    messagePreview: {
      fontFamily: isCompact ? font.base.family : null,
      fontSize: font.base.size.xs,
      color: hasUnreadMessages ? font.color.base : font.color.soft,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      p: {
        margin: 0,
        fontSize: font.base.size.s,
        lineHeight: font.lineHeight.prose,
        fontFamily: isCompact ? font.base.family : null,
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        '&+*': {
          display: 'none'
        }
      }
    }
  };
};

interface MessagingChannelPreviewProps {
  channel: Channel<StreamType>;
  latestMessage?: string | JSX.Element;
  unread?: number;
  onClick: (attendeeId?: string) => void;
  variant?: 'compact' | 'standard';
}

function MessagingChannelPreview({
  channel,
  latestMessage,
  unread,
  onClick,
  variant = 'standard'
}: MessagingChannelPreviewProps): JSX.Element {
  const { translate } = useTranslate();
  const router = useRouter();
  const {
    query: { eventId }
  } = router;
  const { setCurrentChannel, getAttendeeData } = useAttendeeMessaging();
  const { channel: activeChannel, client } = useContext(ChatContext);
  const timestamp = useTimeStamp(channel);

  const members = Object.values(channel.state.members).filter(({ user }) => user.id !== client.userID);
  const isGroup = channel.type === ChannelType.GROUP_MESSAGING;

  let firstNameInStream = '';
  let lastNameInStream = '';
  if (!isGroup) {
    const { firstName, lastName } = getNameInStream(members);
    firstNameInStream = firstName;
    lastNameInStream = lastName;
  }

  const streamChannelName = channel?.data?.name;
  const channelName = useChannelName(members, streamChannelName);
  const isActiveChannel = channel?.id === activeChannel?.id;

  const hasUnreadMessages = !!unread;
  const styles = useStyle({
    isActiveChannel,
    hasUnreadMessages,
    variant
  });

  // Dont show channels that are not attendee-messaging or group-messaging type
  // The stream ChannelList is not honoring the exisiting channel type filter
  // defined in getChannelListCriteria.js at a higher level, hence adding an extra check here.
  if (channel.type !== ChannelType.ATTENDEE_MESSAGING && channel.type !== ChannelType.GROUP_MESSAGING) {
    return null;
  }
  const otherAttendeeId = members[0]?.user?.attendeeId;
  const attendeeData = getAttendeeData(otherAttendeeId);
  const { profilePicture = null } = attendeeData || {};
  const previewOnClick = () => {
    setCurrentChannel({ currentChannel: channel });
    if (onClick) {
      onClick(otherAttendeeId);
    }
  };

  const preview = (
    <>
      {hasUnreadMessages && (
        <div
          {...injectTestId('attendee-messaging-new-message-indicator')}
          css={styles.unreadMessagesIndicator}
          aria-label={translate('AttendeeMessaging-NewMessageIndicator-AccessibilityLabel')}
        />
      )}
      <div css={styles.avatarWrapper}>
        <Avatar
          text={translate('Universal-AvatarName', {
            firstName: firstNameInStream,
            lastName: lastNameInStream
          })}
          size={variant === 'compact' ? 2 : 3}
          image={!isGroup ? profilePicture : <Icon name="person-group" size="sm" aria-hidden />}
          testID="channel-preview-avatar"
          // To prevent avatar flickering as component remounts via stream
          isLoading={false}
        />
      </div>
      <div css={styles.contentWrapper}>
        <div css={styles.channelNameWrapper}>
          <div css={styles.channelName} {...injectTestId('channel-preview-name')}>
            {channelName}
          </div>
          <div css={styles.time} {...injectTestId('channel-preview-timestamp')}>
            {timestamp}
          </div>
        </div>
        {latestMessage && (
          <div css={styles.messagePreview} {...injectTestId('channel-preview-latest-message')}>
            {latestMessage}
          </div>
        )}
      </div>
    </>
  );

  if (variant === 'compact') {
    return (
      <Link
        href={
          !isGroup
            ? `/events/${eventId}/messages?attendeeId=${otherAttendeeId}`
            : `/events/${eventId}/messages?channelId=${channel.id}`
        }
        passHref
        css={styles.linkContainer}
        onClick={previewOnClick}
        {...injectTestId('channel-preview-link')}
        {...overrideSessionReplayPrivacyLevel(PRIVACY_LEVEL.MASK)}
      >
        {preview}
      </Link>
    );
  }

  return (
    <button
      type="button"
      className={
        channel?.id === activeChannel?.id ? 'channel-preview__container selected' : 'channel-preview__container'
      }
      css={styles.container}
      onClick={previewOnClick}
      {...injectTestId('channel-preview-button')}
      {...injectCustomDatadogActionName('Channel Preview button')}
      {...overrideSessionReplayPrivacyLevel(PRIVACY_LEVEL.MASK)}
    >
      {preview}
    </button>
  );
}

export default MessagingChannelPreview;
